import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardPaste,
  Container,
  ExternalLink,
  MonitorPlay,
  FolderOpen,
  Plus,
  Save,
  Scale,
  Settings2,
  ShieldCheck,
  Trash2,
  Truck,
  X,
} from "lucide-react";
import Layout from "../../../layout/Layout";
import Packer from "../../../../utils/konteyner/packer";
import {
  apiHata,
  deleteKonteynerPlan,
  getKonteynerContext,
  getKonteynerMusteriler,
  getKonteynerPlan,
  getKonteynerPlanlar,
  hesaplaKonteynerPlan,
  kaydetKonteynerPlan,
  sunumYolu,
} from "../../../../api/planlama/konteynerPlanApi";
import Konteyner3DView from "./Konteyner3DView";
import "./konteyner.css";

// ------------------------------------------------------------------ yardımcılar
const fmt = (v, d = 0) =>
  Number.isFinite(v) ? v.toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "";
const pct = (v) => `%${fmt((v || 0) * 100)}`;
const PALETTE = ["#2f6f98", "#e07b39", "#3f9a6b", "#8e6bbf", "#d4a72c", "#4fa3a5", "#c8504f", "#8c6a4f", "#5b8fd6", "#d672a0"];
const FIELDS = ["kalinlik", "ebat", "boy", "paketIci", "bantEni", "adet"];
const FIELD_LABEL = { kalinlik: "Kalınlık", ebat: "Ebat", boy: "Boy", paketIci: "Paket içi", bantEni: "Bant eni", adet: "Adet" };
const newId = () => "s" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
const emptyRow = () => ({ id: newId(), kalinlik: "", ebat: "", boy: "", paketIci: "", bantEni: "", adet: "", yerlesim: "otomatik" });
const rowFilled = (r) => FIELDS.every((f) => String(r[f] ?? "").trim() !== "");
const petekKesit = (pk) => (pk ? Array.from({ length: pk.rows }, (_, r) => (r % 2 ? pk.b : pk.a)).join("+") : "");

// Deneme siparişleri (motor dosyasında). Ramsteel olanlar talimat Excel'inden alındı.
const ORNEKLER = Packer.SAMPLE_ORDERS;
const PROFIL_KOD = { ramsteel: "RAMSTEEL", northwest: "NORTHWEST", genel: "GENEL" };

const ayarlarFromMusteri = (m, tipler) => {
  const t = tipler?.[m?.konteynerTipi] || tipler?.["40HC"] || {};
  return {
    konteynerTipi: m?.konteynerTipi || "40HC",
    L: t.L, W: t.W, H: t.H, doorH: t.doorH,
    maxKg: m?.maxKg ?? 25000,
    tabanTakoz: m?.tabanTakoz ?? 0,
    araTakoz: m?.araTakoz ?? 0,
    yanBosluk: m?.yanBosluk ?? 0,
    minTavanBosluk: m?.minTavanBosluk ?? 0,
    kalanMode: "yok",
    ortala: true,
  };
};

const motorKurallari = (m) =>
  (m?.kurallar || []).map((r) => ({
    id: String(r.kural._id),
    tip: r.kural.tip,
    ad: r.kural.ad,
    madde: r.madde,
    varsayilan: r.varsayilanAktif,
    parametreler: r.kural.parametreler,
  }));

function modChip(mod) {
  if (!mod) return null;
  const [d, y] = mod.split("-");
  return <span className={`kyp-dz ${d === "petek" ? "petek" : ""} ${y === "enine" ? "enine" : ""}`}>{Packer.MOD_ADI[mod]}</span>;
}

// ------------------------------------------------------------------ sayfa
function KonteynerYuklemePage() {
  const [ctx, setCtx] = useState(null);
  const [musteriler, setMusteriler] = useState([]);
  const [musteriId, setMusteriId] = useState("");
  const [ayarlar, setAyarlar] = useState(null);
  const [aktif, setAktif] = useState({});
  const [rows, setRows] = useState([emptyRow()]);
  const [siparisNo, setSiparisNo] = useState("");
  const [manuel, setManuel] = useState(null);
  const [plans, setPlans] = useState(null);
  const [altIdx, setAltIdx] = useState(0);
  const [selected, setSelected] = useState(1);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showDims, setShowDims] = useState(false);
  const [kayitlar, setKayitlar] = useState({ planlar: [], pagination: null });
  const [kayitAra, setKayitAra] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ baslik: "", not: "", firma: "" });
  const [sonKayit, setSonKayit] = useState(null); // { id, baslik } — sunum sayfası bağlantısı için
  const [hedefAltKey, setHedefAltKey] = useState(null);
  const [autoHesapla, setAutoHesapla] = useState(false);

  const yetki = ctx?.yetkiler || {};
  const musteri = useMemo(() => musteriler.find((m) => m._id === musteriId), [musteriler, musteriId]);
  const kurallar = useMemo(() => motorKurallari(musteri), [musteri]);
  const result = plans?.alternatives?.[altIdx] || null;

  // ---- ilk yükleme
  useEffect(() => {
    (async () => {
      try {
        const [c, m] = await Promise.all([getKonteynerContext(), getKonteynerMusteriler()]);
        setCtx(c.data.data);
        setMusteriler(m.data.data);
        const ilk = m.data.data[0];
        if (ilk) {
          setMusteriId(ilk._id);
          setAyarlar(ayarlarFromMusteri(ilk, c.data.data.konteynerTipleri));
          setAktif(Object.fromEntries(motorKurallari(ilk).map((k) => [k.id, k.varsayilan])));
        }
      } catch (e) {
        setFeedback({ type: "error", message: apiHata(e) });
      }
    })();
  }, []);

  const loadKayitlar = useCallback(async (page = 1, search = "") => {
    try {
      const r = await getKonteynerPlanlar({ page, limit: 8, search });
      setKayitlar(r.data.data);
    } catch (e) {
      /* liste opsiyonel */
    }
  }, []);
  useEffect(() => {
    loadKayitlar(1, "");
  }, [loadKayitlar]);

  // ---- değişiklik
  const markStale = () => {
    if (plans) setStale(true);
  };
  const selectMusteri = (id, { keepRows = true } = {}) => {
    const m = musteriler.find((x) => x._id === id);
    setMusteriId(id);
    // sunumda görünen firma adı: talimat profili gerçek bir müşteriyse onun adı
    setSaveForm((f) => ({ ...f, firma: m && m.kod !== "GENEL" ? m.ad : "" }));
    setAyarlar(ayarlarFromMusteri(m, ctx?.konteynerTipleri));
    setAktif(Object.fromEntries(motorKurallari(m).map((k) => [k.id, k.varsayilan])));
    if (!keepRows) setRows([emptyRow()]);
    markStale();
  };
  const setAyar = (k, v) => {
    setAyarlar((a) => {
      const next = { ...a, [k]: v };
      if (k === "konteynerTipi" && v !== "ozel") Object.assign(next, (({ L, W, H, doorH }) => ({ L, W, H, doorH }))(ctx.konteynerTipleri[v]));
      if (["L", "W", "H", "doorH"].includes(k)) next.konteynerTipi = "ozel";
      return next;
    });
    markStale();
  };
  const updateRow = (id, f, v) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [f]: v } : r)));
    setManuel(null);
    markStale();
  };

  // ---- Excel'den yapıştırma: KALINLIK, EBAT, (boş), BOY, PAKET İÇİ, BANT ENİ, PAKET KG, ADET ya da 6 sütun
  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!text.includes("\t")) return;
    const lines = text.split(/\r?\n/).map((l) => l.split("\t")).filter((c) => c.some((x) => x.trim()));
    const num = (x) => String(x ?? "").trim().replace(/\./g, "").replace(",", ".");
    const parsed = lines
      .map((c) => {
        const pick = c.length >= 8 && !String(c[2]).trim() ? [0, 1, 3, 4, 5, 7] : [0, 1, 2, 3, 4, 5];
        const vals = pick.map((i) => num(c[i]));
        if (vals.some((v) => !v || Number.isNaN(Number(v)))) return null;
        return { ...emptyRow(), kalinlik: vals[0], ebat: vals[1], boy: vals[2], paketIci: vals[3], bantEni: vals[4], adet: vals[5] };
      })
      .filter(Boolean);
    if (!parsed.length) return;
    e.preventDefault();
    setRows((rs) => [...rs.filter(rowFilled), ...parsed]);
    setManuel(null);
    markStale();
    setFeedback({ type: "success", message: `${parsed.length} satır Excel'den eklendi.` });
  };

  const ornekYukle = (id) => {
    const o = ORNEKLER.find((x) => x.id === id);
    if (!o) return;
    const m = musteriler.find((x) => x.kod === PROFIL_KOD[o.profil]) || musteriler.find((x) => x.kod === "GENEL");
    if (m) selectMusteri(m._id);
    setRows(o.rows.map((r) => ({ id: r.id, kalinlik: r.kalinlik, ebat: r.ebat, boy: r.boy, paketIci: r.paketIci, bantEni: r.bantEni, adet: r.adet, yerlesim: "otomatik" })));
    setManuel(o.manuel ? { list: o.manuel, ad: o.manuelAd } : null);
    setSiparisNo(o.id);
    setSaveForm((f) => ({ ...f, firma: o.musteri }));
    setFeedback({ type: "success", message: `Deneme siparişi yüklendi: ${o.not}` });
    markStale();
  };

  // ---- satır hesapları (anlık, motor kopyasıyla)
  const contSpec = ayarlar && { name: ctx?.konteynerTipleri?.[ayarlar.konteynerTipi]?.name || "Özel", L: +ayarlar.L, W: +ayarlar.W, H: +ayarlar.H, doorH: +ayarlar.doorH, maxKg: +ayarlar.maxKg };
  const engineOpts = ayarlar && { ...ayarlar, ...Packer.kuralOpts(kurallar, aktif) };
  const live = useMemo(() => {
    if (!contSpec) return { rowInfo: {}, pkgs: [] };
    return Packer.buildPackages(rows.map((r) => ({ ...r, dizilim: r.yerlesim })), contSpec, engineOpts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, JSON.stringify(ayarlar), JSON.stringify(aktif), kurallar]);
  const colorOf = useCallback((rowId) => PALETTE[Math.max(0, rows.findIndex((r) => String(r.id) === String(rowId))) % PALETTE.length], [rows]);
  const labelOf = useCallback(
    (rowId) => {
      const r = rows.find((x) => String(x.id) === String(rowId));
      return r ? `${r.ebat} × ${r.kalinlik} × ${fmt(+r.boy)}` : rowId;
    },
    [rows]
  );

  // ---- plan hesabı
  const hesapla = async (opts = {}) => {
    const dolu = rows.filter(rowFilled);
    if (!musteriId) return setFeedback({ type: "error", message: "Müşteri seçin." });
    if (!dolu.length) return setFeedback({ type: "error", message: "En az bir dolu sipariş satırı girin." });
    setBusy(true);
    setFeedback(null);
    try {
      const payload = {
        musteriId,
        ayarlar: { ...ayarlar, L: +ayarlar.L, W: +ayarlar.W, H: +ayarlar.H, doorH: +ayarlar.doorH, maxKg: +ayarlar.maxKg, tabanTakoz: +ayarlar.tabanTakoz, araTakoz: +ayarlar.araTakoz, yanBosluk: +ayarlar.yanBosluk, minTavanBosluk: +ayarlar.minTavanBosluk || 0 },
        aktifKurallar: aktif,
        satirlar: dolu.map((r) => ({ id: String(r.id), ...Object.fromEntries(FIELDS.map((f) => [f, Number(String(r[f]).replace(",", "."))])), yerlesim: r.yerlesim || "otomatik" })),
        ...(manuel ? { manuel: manuel.list, manuelAd: manuel.ad } : {}),
      };
      const r = await hesaplaKonteynerPlan(payload);
      const data = r.data.data;
      setPlans(data);
      const key = opts.altKey || hedefAltKey;
      const i = key ? Math.max(0, data.alternatives.findIndex((a) => a.key === key)) : 0;
      setAltIdx(i);
      setHedefAltKey(null);
      setSelected(1);
      setStale(false);
      setTimeout(() => document.getElementById("kyp-alts")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      setFeedback({ type: "error", message: apiHata(e) });
    } finally {
      setBusy(false);
    }
  };

  // ---- kaydet / aç / sil
  const kaydet = async () => {
    if (!result) return;
    try {
      const sat = rows.filter(rowFilled);
      const payload = {
        siparisNo,
        baslik: saveForm.baslik,
        not: saveForm.not,
        musteriAd: saveForm.firma.trim(),
        musteriId,
        ayarlar: { ...ayarlar, L: +ayarlar.L, W: +ayarlar.W, H: +ayarlar.H, doorH: +ayarlar.doorH, maxKg: +ayarlar.maxKg },
        aktifKurallar: aktif,
        satirlar: sat.map((r) => ({ id: String(r.id), ...Object.fromEntries(FIELDS.map((f) => [f, Number(String(r[f]).replace(",", "."))])), yerlesim: r.yerlesim || "otomatik", secilenYerlesim: result.rowInfo?.[r.id]?.mod || "" })),
        alternatif: { key: result.key, title: result.title },
        ozet: {
          konteynerSayisi: result.meta.count,
          altSinir: result.meta.lowerBound,
          toplamKg: Math.round(result.meta.placedKg),
          paketSayisi: result.containers.reduce((s, c) => s + c.placed.length, 0),
          filo: result.meta.fleet,
          konteynerler: result.containers.map((c) => ({
            no: c.no,
            tip: c.cont.name,
            kg: Math.round(c.stats.kg),
            paket: c.stats.packages,
            tonPct: +(c.stats.kgPct * 100).toFixed(1),
            hacimPct: +(c.stats.volPct * 100).toFixed(1),
            satirlar: Object.entries(result.allocationsByRow)
              .filter(([, a]) => a[c.no])
              .map(([satirId, a]) => ({ satirId, paket: a[c.no].packages, kg: Math.round(a[c.no].kg) })),
          })),
        },
        // sunum sayfasında gösterilen 3D görüntü
        goruntu: Packer.shareSnapshot(
          result,
          sat.map((r) => ({ id: String(r.id), etiket: labelOf(r.id), renk: colorOf(r.id), paketIci: Number(r.paketIci) })),
          kurallar.filter((k) => aktif[k.id]).map((k) => ({ ad: k.ad, madde: k.madde }))
        ),
      };
      const kayit = await kaydetKonteynerPlan(payload);
      setSonKayit({ id: kayit.data.data._id, baslik: saveForm.baslik || `${musteri?.ad || ""} ${siparisNo}`.trim() });
      setSaveOpen(false);
      setSaveForm((f) => ({ ...f, baslik: "", not: "" }));
      setFeedback({ type: "success", message: "Yükleme planı kaydedildi." });
      loadKayitlar(1, kayitAra);
    } catch (e) {
      setFeedback({ type: "error", message: apiHata(e) });
    }
  };

  const planAc = async (id) => {
    try {
      const r = await getKonteynerPlan(id);
      const p = r.data.data;
      if (!musteriler.some((m) => m._id === p.musteri)) return setFeedback({ type: "error", message: "Planın müşteri profili artık aktif değil." });
      setMusteriId(p.musteri);
      setAyarlar({ minTavanBosluk: 0, ...p.ayarlar });
      setAktif(p.aktifKurallar || {});
      setRows(p.satirlar.map((s) => ({ id: s.satirId, kalinlik: s.kalinlik, ebat: s.ebat, boy: s.boy, paketIci: s.paketIci, bantEni: s.bantEni, adet: s.adet, yerlesim: s.yerlesim || "otomatik" })));
      setSiparisNo(p.siparisNo || "");
      setSaveForm((f) => ({ ...f, firma: p.musteriAd || "" }));
      setManuel(null);
      setHedefAltKey(p.alternatif?.key || null);
      setPlans(null);
      setFeedback({ type: "success", message: `“${p.baslik || p.siparisNo || "Plan"}” yüklendi, plan yeniden hesaplanıyor…` });
      setAutoHesapla(true);
    } catch (e) {
      setFeedback({ type: "error", message: apiHata(e) });
    }
  };

  const planSil = async (id) => {
    if (!window.confirm("Bu kayıtlı plan silinsin mi?")) return;
    try {
      await deleteKonteynerPlan(id);
      loadKayitlar(1, kayitAra);
    } catch (e) {
      setFeedback({ type: "error", message: apiHata(e) });
    }
  };

  // kayıtlı plan açılınca girdiler yerleştikten sonra otomatik hesapla
  useEffect(() => {
    if (autoHesapla && ayarlar && rows.length) {
      setAutoHesapla(false);
      hesapla();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoHesapla]);

  // ---- türetilmiş
  const container = result?.containers?.find((c) => c.no === selected);
  const doluSatir = rows.filter(rowFilled).length;
  const toplamKg = live.pkgs.reduce((s, p) => s + p.kg, 0);
  const A = plans?.alternatives?.[0];

  if (!ctx || !ayarlar) {
    return (
      <Layout>
        <div className="kyp-page">
          {feedback ? <div className={`kyp-feedback ${feedback.type}`}>{feedback.message}</div> : <div className="kyp-loading"><span className="kyp-spinner" /> Yükleniyor…</div>}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="kyp-page">
        {/* ---------------- başlık */}
        <section className="kyp-hero">
          <div className="kyp-hero-copy">
            <span className="kyp-kicker">
              <Truck size={14} /> Planlama
            </span>
            <h1>Konteyner Yükleme Planı</h1>
            <p>
              Sipariş satırlarını girin, müşterinin yükleme talimatını seçin. Motor düz/petek ve boyuna/enine yöntemleri deneyerek
              <b> en az konteynerli</b> planı ve alternatiflerini çıkarır; her plan fiziksel olarak doğrulanır.
            </p>
          </div>
          <div className="kyp-hero-card">
            <span>Yükleme talimatı</span>
            <strong>{musteri?.ad || "—"}</strong>
            <small>
              {musteri?.talimatNo || "Talimat no yok"} · {ctx.konteynerTipleri[ayarlar.konteynerTipi]?.name || "Özel ölçü"}
            </small>
            {(yetki.musteriYonet || yetki.kuralYonet) && (
              <Link to="/planning/konteyner-kurallar" className="kyp-hero-link">
                <Settings2 size={13} /> Kurallar ve müşteriler
              </Link>
            )}
          </div>
        </section>

        {feedback && (
          <div className={`kyp-feedback ${feedback.type}`} role="alert">
            {feedback.message}
            <button type="button" onClick={() => setFeedback(null)} aria-label="Kapat">
              <X size={14} />
            </button>
          </div>
        )}

        {/* ---------------- sipariş */}
        <section className="kyp-panel">
          <div className="kyp-panel-header">
            <div>
              <h2>1 · Sipariş ve yükleme talimatı</h2>
              <p>Satırları elle girin ya da Excel'den kopyalayıp tabloya yapıştırın.</p>
            </div>
            <div className="kyp-actions">
              <select className="kyp-sample" value="" onChange={(e) => ornekYukle(e.target.value)} aria-label="Deneme siparişi yükle">
                <option value="">Deneme siparişi yükle…</option>
                {ORNEKLER.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.id} · {o.musteri}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="kyp-form-grid">
            <div className="kyp-field">
              <label htmlFor="kyp-musteri">Müşteri / talimat</label>
              <select id="kyp-musteri" value={musteriId} onChange={(e) => selectMusteri(e.target.value)}>
                {musteriler.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.ad}
                    {m.talimatNo ? ` · ${m.talimatNo}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-siparis">Sipariş no</label>
              <input id="kyp-siparis" value={siparisNo} onChange={(e) => setSiparisNo(e.target.value)} placeholder="ör. SP-2026-0418" />
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-tip">Konteyner tipi</label>
              <select id="kyp-tip" value={ayarlar.konteynerTipi} onChange={(e) => setAyar("konteynerTipi", e.target.value)}>
                {Object.entries(ctx.konteynerTipleri).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.name}
                  </option>
                ))}
                <option value="ozel">Özel ölçü</option>
              </select>
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-maxkg">Max tonaj (kg)</label>
              <input id="kyp-maxkg" type="number" value={ayarlar.maxKg} onChange={(e) => setAyar("maxKg", e.target.value)} />
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-taban">Taban takozu (mm)</label>
              <input id="kyp-taban" type="number" value={ayarlar.tabanTakoz} onChange={(e) => setAyar("tabanTakoz", e.target.value)} />
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-ara">Ara takoz (mm)</label>
              <input id="kyp-ara" type="number" value={ayarlar.araTakoz} onChange={(e) => setAyar("araTakoz", e.target.value)} />
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-yan">Toplam yan boşluk (mm)</label>
              <input id="kyp-yan" type="number" value={ayarlar.yanBosluk} onChange={(e) => setAyar("yanBosluk", e.target.value)} />
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-tavan">Tavana en az boşluk (mm)</label>
              <input id="kyp-tavan" type="number" min="0" step="50" value={ayarlar.minTavanBosluk ?? 0} onChange={(e) => setAyar("minTavanBosluk", e.target.value)} aria-describedby="kyp-tavan-hint" />
              <small id="kyp-tavan-hint" className="kyp-muted">
                {+ayarlar.minTavanBosluk > 0
                  ? `Yük en fazla ${fmt(Math.min(+ayarlar.H - +ayarlar.minTavanBosluk, +ayarlar.doorH))} mm yüksekliğe çıkar (forklift halat payı).`
                  : "Forkliftle üstten halattan kaldırma payı. 1000 mm = 100 cm."}
              </small>
            </div>
            <div className="kyp-field">
              <label htmlFor="kyp-kalan">Pakete girmeyen parçalar</label>
              <select id="kyp-kalan" value={ayarlar.kalanMode} onChange={(e) => setAyar("kalanMode", e.target.value)}>
                <option value="yok">Plana alma</option>
                <option value="paket">Ayrı küçük paket yap</option>
              </select>
            </div>
          </div>

          <div className="kyp-subrow">
            <label className="kyp-check">
              <input type="checkbox" checked={!!ayarlar.ortala} onChange={(e) => setAyar("ortala", e.target.checked)} /> Yükü ortala
            </label>
            <button type="button" className="kyp-link-btn" onClick={() => setShowDims(!showDims)}>
              {showDims ? "İç ölçüleri gizle" : `İç ölçüler: ${fmt(+ayarlar.L)} × ${fmt(+ayarlar.W)} × ${fmt(+ayarlar.H)} mm, kapı ${fmt(+ayarlar.doorH)} mm`}
            </button>
          </div>
          {showDims && (
            <div className="kyp-form-grid dims">
              {[["L", "İç boy"], ["W", "İç en"], ["H", "İç yükseklik"], ["doorH", "Kapı yüksekliği"]].map(([k, l]) => (
                <div className="kyp-field" key={k}>
                  <label htmlFor={`kyp-${k}`}>{l} (mm)</label>
                  <input id={`kyp-${k}`} type="number" value={ayarlar[k]} onChange={(e) => setAyar(k, e.target.value)} />
                </div>
              ))}
            </div>
          )}

          <fieldset className="kyp-rules">
            <legend>
              <ShieldCheck size={14} /> Yükleme kuralları · {musteri?.ad}
            </legend>
            {kurallar.length ? (
              kurallar.map((k) => (
                <label key={k.id} className="kyp-check" title={musteri?.kurallar?.find((r) => String(r.kural._id) === k.id)?.kural?.aciklama || ""}>
                  <input
                    type="checkbox"
                    checked={aktif[k.id] ?? k.varsayilan}
                    onChange={(e) => {
                      setAktif((a) => ({ ...a, [k.id]: e.target.checked }));
                      markStale();
                    }}
                  />
                  {k.ad}
                  {k.madde && <span className="kyp-madde">{k.madde}</span>}
                </label>
              ))
            ) : (
              <span className="kyp-muted">Bu müşteriye kural tanımlanmamış; yalnız düz boyuna yükleme yapılır.</span>
            )}
          </fieldset>

          <div className="kyp-table-wrap" onPaste={onPaste}>
            <table className="kyp-table kyp-input-table">
              <thead>
                <tr>
                  <th />
                  {FIELDS.map((f) => (
                    <th key={f}>{FIELD_LABEL[f]}</th>
                  ))}
                  <th>Yerleşim</th>
                  <th className="num">Paket kg</th>
                  <th className="num">Paket</th>
                  <th className="num">Kalan</th>
                  <th className="num">Ton</th>
                  <th className="num">Paket eni</th>
                  <th className="num">Paket yük.</th>
                  <th>Durum</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const info = live.rowInfo[r.id];
                  const c = info?.calc;
                  const planInfo = !stale ? result?.rowInfo?.[r.id] : null;
                  return (
                    <tr key={r.id} className={info?.errors?.length ? "has-error" : ""}>
                      <td>
                        <span className="kyp-swatch" style={{ background: colorOf(r.id) }} />
                      </td>
                      {FIELDS.map((f) =>
                        f === "paketIci" ? (
                          <td key={f}>
                            <select value={String(r.paketIci)} onChange={(e) => updateRow(r.id, f, e.target.value)} aria-label="Paket içi">
                              <option value="" />
                              {Object.keys(Packer.PAKET_ICI_MAP).map((k) => (
                                <option key={k}>{k}</option>
                              ))}
                            </select>
                          </td>
                        ) : (
                          <td key={f}>
                            <input type="number" step="any" min="0" value={r[f]} onChange={(e) => updateRow(r.id, f, e.target.value)} aria-label={FIELD_LABEL[f]} />
                          </td>
                        )
                      )}
                      <td>
                        <select className="wide" value={r.yerlesim || "otomatik"} onChange={(e) => updateRow(r.id, "yerlesim", e.target.value)} aria-label="Yerleşim">
                          {ctx.yerlesimModlari.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="num calc">{c?.paketKg ? fmt(c.paketKg) : ""}</td>
                      <td className="num calc">{c?.paketIci ? fmt(c.paketSayisi) : ""}</td>
                      <td className="num calc">{c?.paketIci ? fmt(c.kalanAdet) : ""}</td>
                      <td className="num calc">{c?.totalKg ? fmt(c.totalTons, 1) : ""}</td>
                      <td className="num calc">{c?.paketEni ? fmt(c.paketEni) : ""}</td>
                      <td className="num calc">{c?.paketYuk ? fmt(c.paketYuk) : ""}</td>
                      <td className="kyp-rowmsg">
                        {info?.errors?.map((m, i) => (
                          <div key={i} className="e">{m}</div>
                        ))}
                        {info?.warnings?.map((m, i) => (
                          <div key={i} className="w">{m}</div>
                        ))}
                        {!info?.errors?.length && info?.packages > 0 && (
                          planInfo?.packages ? (
                            <>
                              <ModDagilim modlar={result.rowModlar?.[r.id]} fallback={planInfo.mod} />
                              {planInfo.dizilim === "petek" && <span className="kyp-muted"> kesit {petekKesit(planInfo.petek)}</span>}
                              <div className="kyp-muted">
                                {info.dizilimKaynak?.startsWith("Madde") ? info.dizilimKaynak.split(":")[0] : info.modlar.length > 1 ? "plan seçti" : ""}
                                {info.enineNeden ? ` · ${info.enineNeden}` : ""}
                              </div>
                            </>
                          ) : (
                            <>
                              {info.modlar.length > 1 ? <span className="kyp-muted">{info.modlar.length} yöntem denenecek: {info.modlar.map((m) => Packer.MOD_ADI[m]).join(", ")}</span> : modChip(info.mod)}
                              {info.enineNeden && <div className="kyp-muted">{info.enineNeden}</div>}
                            </>
                          )
                        )}
                      </td>
                      <td>
                        <div className="kyp-row-actions">
                          <button type="button" className="kyp-icon-btn" title="Satırı kopyala" onClick={() => setRows((rs) => { const i = rs.findIndex((x) => x.id === r.id); const n = [...rs]; n.splice(i + 1, 0, { ...r, id: newId() }); return n; })}>
                            <Plus size={14} />
                          </button>
                          <button type="button" className="kyp-icon-btn danger" title="Satırı sil" onClick={() => { setRows((rs) => (rs.length > 1 ? rs.filter((x) => x.id !== r.id) : [emptyRow()])); markStale(); }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="kyp-actionbar">
            <div className="kyp-actions">
              <button type="button" className="kyp-secondary-btn" onClick={() => setRows((rs) => [...rs, emptyRow()])}>
                <Plus size={15} /> Satır ekle
              </button>
              <span className="kyp-muted kyp-paste-hint">
                <ClipboardPaste size={14} /> Excel'den kopyalayıp tabloya yapıştırabilirsiniz
              </span>
            </div>
            <div className="kyp-actions">
              <span className="kyp-muted">
                {fmt(doluSatir)} satır · {fmt(live.pkgs.length)} paket · {fmt(toplamKg / 1000, 1)} t
              </span>
              <button type="button" className="kyp-primary-btn" onClick={() => hesapla()} disabled={busy}>
                {busy ? <span className="kyp-spinner light" /> : <Container size={16} />} {busy ? "Hesaplanıyor…" : "Yükleme planı çıkar"}
              </button>
            </div>
          </div>
        </section>

        {stale && plans && (
          <div className="kyp-warning">
            <AlertTriangle size={15} /> Sipariş ya da kurallar değişti. Aşağıdaki planlar önceki girdilere ait; güncellemek için <b>Yükleme planı çıkar</b>'a basın.
          </div>
        )}

        {/* ---------------- alternatifler */}
        {plans && (
          <div className={stale ? "kyp-stale" : ""}>
            <section id="kyp-alts" className="kyp-section">
              <div className="kyp-section-head">
                <div>
                  <span className="kyp-eyebrow">Adım 2</span>
                  <h2>Alternatif yükleme planları</h2>
                </div>
                <span className="kyp-muted">
                  {plans.alternatives.length} plan · {fmt(plans.ms)} ms
                </span>
              </div>
              {!A?.meta?.count ? (
                <div className="kyp-empty">Plan çıkmadı. Satırlardaki Durum sütununu kontrol edin.</div>
              ) : (
                <div className="kyp-alts">
                  {plans.alternatives.map((a, i) => (
                    <AltCard key={a.key + i} a={a} A={A} i={i} active={i === altIdx} onClick={() => { setAltIdx(i); setSelected(1); }} />
                  ))}
                </div>
              )}
              <div className="kyp-legend">
                <span><i className="kg" /> Ton doluluğu</span>
                <span><i className="vol" /> Hacim doluluğu</span>
                <span>“·20” = 20'lik konteyner</span>
              </div>
            </section>

            {result && result.meta.count > 0 && (
              <>
                <section className="kyp-summary">
                  <Stat icon={<Container size={20} />} label="Seçili plan" value={result.title} small={Object.entries(result.meta.fleet).map(([n, k]) => `${k} × ${n}`).join(" + ")} />
                  <Stat icon={<Truck size={20} />} label="Konteyner" value={fmt(result.meta.count)} small={result.meta.count === result.meta.lowerBound && Object.keys(result.meta.fleet).length === 1 ? "Alt sınıra eşit — daha azı imkânsız" : `Alt sınır ${fmt(result.meta.lowerBound)}`} />
                  <Stat icon={<Scale size={20} />} label="Ortalama doluluk" value={pct(result.meta.avgKgPct)} small={`${fmt(result.meta.placedKg / 1000, 1)} t · hacim ${pct(result.meta.avgVolPct)}`} />
                  <Stat
                    icon={result.dogrulama?.ok ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                    label="Doğrulama"
                    value={result.dogrulama?.ok ? "Geçti" : "Sorun var"}
                    small={`${result.dogrulama?.checks.filter((c) => c.ok).length}/${result.dogrulama?.checks.length} kontrol`}
                    tone={result.dogrulama?.ok ? "ok" : "bad"}
                  />
                  {yetki.planKaydet && (
                    <button type="button" className="kyp-primary-btn kyp-save-btn" onClick={() => setSaveOpen(true)}>
                      <Save size={16} /> Planı kaydet
                    </button>
                  )}
                </section>

                {saveOpen && (
                  <section className="kyp-panel kyp-save">
                    <div className="kyp-form-grid">
                      <div className="kyp-field">
                        <label htmlFor="kyp-baslik">Başlık</label>
                        <input id="kyp-baslik" value={saveForm.baslik} onChange={(e) => setSaveForm((f) => ({ ...f, baslik: e.target.value }))} placeholder={`${musteri?.ad || ""} ${siparisNo}`.trim()} />
                      </div>
                      <div className="kyp-field">
                        <label htmlFor="kyp-firma">Müşteri firma adı</label>
                        <input id="kyp-firma" value={saveForm.firma} onChange={(e) => setSaveForm((f) => ({ ...f, firma: e.target.value }))} placeholder={musteri?.ad || ""} />
                      </div>
                      <div className="kyp-field span2">
                        <label htmlFor="kyp-not">Not</label>
                        <input id="kyp-not" value={saveForm.not} onChange={(e) => setSaveForm((f) => ({ ...f, not: e.target.value }))} />
                      </div>
                    </div>
                    <div className="kyp-actionbar">
                      <span className="kyp-muted">
                        “{result.title}” planı, {siparisNo ? `${siparisNo} siparişi ve ` : ""}girdileriyle birlikte kaydedilir.
                      </span>
                      <div className="kyp-actions">
                        <button type="button" className="kyp-secondary-btn" onClick={() => setSaveOpen(false)}>
                          Vazgeç
                        </button>
                        <button type="button" className="kyp-primary-btn" onClick={kaydet}>
                          <Save size={15} /> Kaydet
                        </button>
                      </div>
                    </div>
                  </section>
                )}

                {sonKayit && <SunumKutusu k={sonKayit} onClose={() => setSonKayit(null)} />}

                <section className="kyp-plan-grid">
                  <Konteyner3DView
                    result={result}
                    no={selected}
                    colorOf={colorOf}
                    labelOf={labelOf}
                    title={container ? `${result.title} · ${container.no}. konteyner (${container.cont.name})` : result.title}
                  />
                  <aside className="kyp-side">
                    <ContainerList result={result} selected={selected} onSelect={setSelected} />
                    {container && <ContainerDetail c={container} rows={rows} colorOf={colorOf} labelOf={labelOf} />}
                    <Checks result={result} />
                    <Talimat musteri={musteri} result={result} container={container} A={A} labelOf={labelOf} />
                  </aside>
                </section>

                <Allocation result={result} rows={rows} colorOf={colorOf} />
              </>
            )}
          </div>
        )}

        {/* ---------------- kayıtlı planlar */}
        <section className="kyp-panel">
          <div className="kyp-panel-header">
            <div>
              <h2>
                <FolderOpen size={17} /> Kayıtlı planlar
              </h2>
              <p>{yetki.tumPlanlar ? "Tüm kullanıcıların kaydettiği planlar." : "Kaydettiğiniz planlar."}</p>
            </div>
            <form
              className="kyp-search"
              onSubmit={(e) => {
                e.preventDefault();
                loadKayitlar(1, kayitAra);
              }}
            >
              <input value={kayitAra} onChange={(e) => setKayitAra(e.target.value)} placeholder="Sipariş, müşteri, başlık…" aria-label="Kayıtlı planlarda ara" />
              <button type="submit" className="kyp-secondary-btn sm">Ara</button>
            </form>
          </div>
          {kayitlar.planlar.length ? (
            <div className="kyp-table-wrap">
              <table className="kyp-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Sipariş</th>
                    <th>Başlık</th>
                    <th>Müşteri</th>
                    <th>Plan</th>
                    <th className="num">Konteyner</th>
                    <th className="num">Ton</th>
                    <th>Kaydeden</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {kayitlar.planlar.map((p) => (
                    <tr key={p._id}>
                      <td>{new Date(p.createdAt).toLocaleDateString("tr-TR")}</td>
                      <td className="mono">{p.siparisNo || "—"}</td>
                      <td>{p.baslik || "—"}</td>
                      <td>{p.musteriAd}</td>
                      <td>{p.alternatif?.title}</td>
                      <td className="num">{fmt(p.ozet?.konteynerSayisi)}</td>
                      <td className="num">{fmt((p.ozet?.toplamKg || 0) / 1000, 1)}</td>
                      <td>{p.createdByName}</td>
                      <td>
                        <div className="kyp-row-actions">
                          <button type="button" className="kyp-secondary-btn sm" onClick={() => planAc(p._id)}>
                            Aç
                          </button>
                          <Link className="kyp-secondary-btn sm" to={sunumYolu(p._id)} title="Konteynerleri 3D sunum sayfasında aç">
                            <MonitorPlay size={14} /> Sunum
                          </Link>
                          {yetki.planKaydet && (
                            <button type="button" className="kyp-icon-btn danger" title="Planı sil" onClick={() => planSil(p._id)}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {kayitlar.pagination?.totalPages > 1 && (
                <div className="kyp-pagination">
                  <span>
                    Sayfa {kayitlar.pagination.page} / {kayitlar.pagination.totalPages} · {kayitlar.pagination.total} plan
                  </span>
                  <div>
                    <button type="button" disabled={kayitlar.pagination.page <= 1} onClick={() => loadKayitlar(kayitlar.pagination.page - 1, kayitAra)}>Önceki</button>
                    <button type="button" disabled={kayitlar.pagination.page >= kayitlar.pagination.totalPages} onClick={() => loadKayitlar(kayitlar.pagination.page + 1, kayitAra)}>Sonraki</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="kyp-empty">Kayıtlı plan yok. Bir plan hesaplayıp “Planı kaydet” ile saklayabilirsiniz.</div>
          )}
        </section>
      </div>
    </Layout>
  );
}

// ------------------------------------------------------------------ alt bileşenler

function SunumKutusu({ k, onClose }) {
  return (
    <section className="kyp-panel kyp-share">
      <div className="kyp-share-ic">
        <MonitorPlay size={20} />
      </div>
      <div className="kyp-share-body">
        <b>Sunum sayfası hazır{k.baslik ? ` · ${k.baslik}` : ""}</b>
        <span className="kyp-muted">Müşteri, sipariş, sıralı konteynerler, 3D yerleşim ve tavan boşluğu tek sayfada. Konteyner planı görme yetkisi olan kullanıcılar açabilir.</span>
        <div className="kyp-share-url">
          <Link className="kyp-primary-btn sm" to={sunumYolu(k.id)}>
            <ExternalLink size={14} /> Sunumu aç
          </Link>
        </div>
      </div>
      <button type="button" className="kyp-icon-btn" title="Kapat" onClick={onClose}>
        <X size={15} />
      </button>
    </section>
  );
}

function ModDagilim({ modlar, fallback }) {
  const list = Object.entries(modlar || {});
  if (!list.length) return modChip(fallback);
  if (list.length === 1) return modChip(list[0][0]);
  return (
    <span className="kyp-moddagilim">
      {list.map(([m, n]) => (
        <span key={m}>
          {modChip(m)} <b>{fmt(n)}</b>
        </span>
      ))}
    </span>
  );
}

function Stat({ icon, label, value, small, tone }) {
  return (
    <div className={`kyp-stat ${tone || ""}`}>
      <div className="kyp-stat-icon">{icon}</div>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        {small && <span>{small}</span>}
      </div>
    </div>
  );
}

function AltCard({ a, A, i, active, onClick }) {
  const m = a.meta;
  const rowsN = Object.values(a.rowInfo).filter((x) => x.packages).length;
  const spread = m.maxKgPct - m.minKgPct;
  const spreadA = A.meta.maxKgPct - A.meta.minKgPct;
  const cmp = (v, ref, higherBetter) => (i === 0 || Math.abs(v - ref) < 1e-9 ? "" : (v > ref) === higherBetter ? "better" : "worse");
  let badge = null;
  if (i === 0) badge = <span className="kyp-pill ok">Önerilen</span>;
  else if (a.key === "manuel") badge = <span className={`kyp-pill ${a.manualFits && !a.unplaced.length ? "warn" : "bad"}`}>Elle yapılmış</span>;
  else if (a.key === "duzboyuna") badge = <span className={`kyp-pill ${m.count > A.meta.count ? "bad" : "warn"}`}>Karşılaştırma</span>;
  else if (a.excludedRows) badge = <span className="kyp-pill bad">Eksik {a.excludedRows} satır</span>;
  else if (m.count > A.meta.count) badge = <span className="kyp-pill warn">+{m.count - A.meta.count} konteyner</span>;
  return (
    <button type="button" className="kyp-alt" aria-pressed={active} onClick={onClick}>
      <div className="kyp-alt-top">
        <h3>{a.title}</h3>
        {badge}
      </div>
      <div>
        <span className="kyp-alt-big">
          {fmt(m.count)}
          <small>konteyner</small>
        </span>
        <div className="kyp-alt-fleet">{Object.entries(m.fleet).map(([n, k]) => `${k} × ${n}`).join(" + ")}</div>
      </div>
      <div className="kyp-mini" aria-label="Konteyner başına doluluk">
        {a.containers.map((c) => (
          <div className="g" key={c.no} title={`${c.no}. konteyner · ton ${pct(c.stats.kgPct)} · hacim ${pct(c.stats.volPct)}`}>
            <div className="bs">
              <div className="b kg" style={{ height: `${Math.min(100, c.stats.kgPct * 100)}%` }} />
              <div className="b vol" style={{ height: `${Math.min(100, c.stats.volPct * 100)}%` }} />
            </div>
            <div className="n">
              {c.no}
              {c.cont.L < 8000 ? "·20" : ""}
            </div>
          </div>
        ))}
      </div>
      <dl className="kyp-alt-metrics">
        <dt>Ort. ton doluluğu</dt>
        <dd className={cmp(m.avgKgPct, A.meta.avgKgPct, true)}>{pct(m.avgKgPct)}</dd>
        <dt>Ort. hacim doluluğu</dt>
        <dd>{pct(m.avgVolPct)}</dd>
        <dt>En boş konteyner</dt>
        <dd className={cmp(m.minKgPct, A.meta.minKgPct, true)}>{pct(m.minKgPct)}</dd>
        <dt>Doluluk farkı</dt>
        <dd className={cmp(spread, spreadA, false)}>{fmt(spread * 100)} puan</dd>
        <dt>Bölünen ürün</dt>
        <dd className={cmp(m.splits - rowsN, A.meta.splits - rowsN, false)}>{fmt(m.splits - rowsN)}</dd>
        <dt>Denge uyarısı</dt>
        <dd>{fmt(a.warnings.length)}</dd>
      </dl>
      {a.desc && <p className="kyp-alt-desc">{a.desc}</p>}
    </button>
  );
}

function ContainerList({ result, selected, onSelect }) {
  return (
    <div className="kyp-panel">
      <div className="kyp-panel-header compact">
        <h3>Konteynerler</h3>
        <span className="kyp-muted">{result.containers.length} adet</span>
      </div>
      <div className="kyp-clist">
        {result.containers.map((c) => (
          <button key={c.no} type="button" className="kyp-cbtn" aria-current={c.no === selected} onClick={() => onSelect(c.no)}>
            <span className="no">{c.no}</span>
            <span className="bars">
              <span className="bar">
                <span>Ton</span>
                <span className="track"><span className="fill" style={{ width: `${Math.min(100, c.stats.kgPct * 100)}%` }} /></span>
                <span className="val">{pct(c.stats.kgPct)}</span>
              </span>
              <span className="bar">
                <span>Hacim</span>
                <span className="track"><span className="fill vol" style={{ width: `${Math.min(100, c.stats.volPct * 100)}%` }} /></span>
                <span className="val">{pct(c.stats.volPct)}</span>
              </span>
              <span className="meta">
                {c.cont.name} · {fmt(c.stats.kg)} kg · {fmt(c.stats.packages)} paket
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ContainerDetail({ c, colorOf, labelOf }) {
  const s = c.stats;
  const by = {};
  for (const p of c.placed) {
    const b = (by[p.rowId] ||= { n: 0, kg: 0 });
    b.n++;
    b.kg += p.kg;
  }
  return (
    <div className="kyp-panel">
      <div className="kyp-panel-header compact">
        <h3>
          {c.no}. konteyner · {c.cont.name}
        </h3>
      </div>
      <div className="kyp-panel-body">
        <dl className="kyp-kv">
          <dt>Yük</dt>
          <dd>{fmt(s.kg)} / {fmt(c.U.maxKg)} kg</dd>
          <dt>Paket</dt>
          <dd>{fmt(s.packages)}</dd>
          <dt>Ton / hacim doluluğu</dt>
          <dd>{pct(s.kgPct)} / {pct(s.volPct)}</dd>
          <dt>Yük boyu × yüksekliği</dt>
          <dd>{fmt(s.loadLen)} × {fmt(s.loadHeight)} mm</dd>
          <dt>Tavana boşluk</dt>
          <dd className="kyp-accent-txt">
            {fmt(s.tavanBosluk)} mm{" "}
            <span className="kyp-muted">
              ({c.U.minTavan ? `en az ${fmt(c.U.minTavan)} istendi, ` : ""}kapı üstüne {fmt(s.kapiBosluk)} mm)
            </span>
          </dd>
          <dt>Ağırlık merkezi ende</dt>
          <dd>{pct(s.cogPctY)}</dd>
        </dl>
        <div className="kyp-cog">
          <div className="kyp-eyebrow">Ağırlık merkezi · boyda</div>
          <div className="kyp-cog-track">
            <div className="mid" />
            <div className="mk" style={{ left: `${Math.max(0, Math.min(100, s.cogPctX * 100))}%` }} />
          </div>
          <div className="kyp-cog-cap">
            <span>arka duvar</span>
            <span>{pct(s.cogPctX)}</span>
            <span>kapı</span>
          </div>
        </div>
        <div className="kyp-mix">
          {Object.entries(by).map(([id, b]) => (
            <div key={id}>
              <span className="kyp-swatch" style={{ background: colorOf(id) }} />
              <span>{labelOf(id)}</span>
              <span className="mono">
                {fmt(b.n)} pk · {fmt(b.kg)} kg
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Checks({ result }) {
  const v = result.dogrulama;
  if (!v) return null;
  return (
    <div className="kyp-panel">
      <div className="kyp-panel-header compact">
        <h3>Doğrulama</h3>
        <span className={`kyp-pill ${v.ok ? "ok" : "bad"}`}>{v.ok ? "Tutarlı" : "Sorun var"}</span>
      </div>
      <ul className="kyp-checks">
        {v.checks.map((c) => (
          <li key={c.name} className={c.ok ? "ok" : "no"}>
            <span className="ic">{c.ok ? "✓" : "✗"}</span>
            <span>
              {c.name}
              <small>{c.detail}</small>
            </span>
          </li>
        ))}
      </ul>
      {result.warnings.length > 0 && (
        <ul className="kyp-warns">
          {result.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Talimat({ musteri, result, container, A, labelOf }) {
  const rap = A?.modRaporu || {};
  const neden = Object.entries(rap).filter(([, sc]) => sc.length > 1);
  const by = {};
  for (const p of container?.placed || []) (by[`${p.rowId}|${p.mod}`] ||= []).push(p);
  const U = container?.U;
  return (
    <div className="kyp-panel">
      <div className="kyp-panel-header compact">
        <h3>Yükleme talimatı</h3>
        <span className="kyp-muted">{musteri?.talimatNo || musteri?.ad}</span>
      </div>
      <div className="kyp-panel-body kyp-talimat">
        {neden.length > 0 && (
          <div>
            <div className="kyp-eyebrow">Yöntem seçimi · paket/konteyner</div>
            <ul>
              {neden.map(([id, sc]) => {
                const chosen = result.rowInfo?.[id]?.mod;
                return (
                  <li key={id}>
                    {labelOf(id)}:{" "}
                    {sc.map((x, i) => (
                      <span key={x.mod}>
                        {i > 0 && " · "}
                        {x.mod === chosen ? <b>{Packer.MOD_ADI[x.mod]} {fmt(x.capGeo)}</b> : `${Packer.MOD_ADI[x.mod]} ${fmt(x.capGeo)}`}
                      </span>
                    ))}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {container && (
          <div>
            <div className="kyp-eyebrow">Bu konteyner</div>
            <ul>
              {Object.entries(by).map(([key, list]) => {
                const id = key.split("|")[0];
                const info = result.rowInfo[id];
                const extra = list[0].petek
                  ? `${new Set(list.map((p) => p.blockId)).size} bölüm${info?.petek ? `, kesit ${petekKesit(info.petek)}` : ""}`
                  : `${new Set(list.map((p) => Math.round(p.z))).size} kat`;
                return (
                  <li key={key}>
                    {modChip(list[0].mod)} {labelOf(id)}: {fmt(list.length)} paket, {extra}
                    {info?.dizilimKaynak?.startsWith("Madde") && <span className="kyp-muted"> ({info.dizilimKaynak.split(":")[0]})</span>}
                  </li>
                );
              })}
              {U && (
                <li>
                  Taban takozu {fmt(U.taban)} mm{U.ara ? `, ara takoz ${fmt(U.ara)} mm (düz katlar arasında)` : ""}
                  {container.stats.petek ? "; petek sıralar arasında ara takoz yok" : ""}.
                </li>
              )}
              {U && <li>{U.farkliBoyYasak ? "Farklı boylar üst üste yüklenmedi (talimat kuralı)." : "Farklı boylar üst üste yüklenebilir."}</li>}
            </ul>
          </div>
        )}
        {musteri?.talimatMaddeleri?.length > 0 && (
          <details>
            <summary>Talimat maddeleri ({musteri.talimatMaddeleri.length})</summary>
            <ul>
              {musteri.talimatMaddeleri.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
            {musteri.talimatKaynak && <p className="kyp-src">{musteri.talimatKaynak}</p>}
          </details>
        )}
      </div>
    </div>
  );
}

function Allocation({ result, rows, colorOf }) {
  const cs = result.containers;
  const list = rows.filter((r) => result.rowInfo[r.id]?.packages || result.rowInfo[r.id]?.errors?.length);
  return (
    <section className="kyp-panel">
      <div className="kyp-panel-header">
        <div>
          <h2>Konteyner dağılımı</h2>
          <p>{result.title}</p>
        </div>
      </div>
      <div className="kyp-table-wrap">
        <table className="kyp-table kyp-alloc">
          <thead>
            <tr>
              <th />
              <th className="num">Kalınlık</th>
              <th className="num">Ebat</th>
              <th className="num">Boy</th>
              <th className="num">Paket içi</th>
              <th className="num">Paket kg</th>
              <th className="num">Paket</th>
              <th>Yerleşim</th>
              {cs.map((c) => (
                <th key={c.no} className="grp" colSpan={2}>
                  {c.no}. konteyner{c.cont.L < 8000 ? " (20')" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((r) => {
              const info = result.rowInfo[r.id];
              const c = info.calc;
              const a = result.allocationsByRow[String(r.id)] || {};
              return (
                <tr key={r.id}>
                  <td>
                    <span className="kyp-swatch" style={{ background: colorOf(r.id) }} />
                  </td>
                  <td className="num">{fmt(c.kalinlik, 2)}</td>
                  <td className="num">{fmt(c.ebat, 1)}</td>
                  <td className="num">{fmt(c.boy)}</td>
                  <td className="num">{fmt(c.paketIci)}</td>
                  <td className="num">{fmt(c.paketKg)}</td>
                  <td className="num">{fmt(c.paketSayisi)}</td>
                  <td>{info.packages ? <ModDagilim modlar={result.rowModlar?.[r.id]} fallback={info.mod} /> : <span className="kyp-pill bad">Plan dışı</span>}</td>
                  {cs.map((k) => (
                    <FragmentCells key={k.no} v={a[k.no]} />
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={8} className="num">Konteyner toplamı</td>
              {cs.map((c) => (
                <FragmentCells key={c.no} v={{ packages: c.placed.length, kg: c.kg }} />
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function FragmentCells({ v }) {
  return (
    <>
      <td className="num grp">{v ? fmt(v.packages) : ""}</td>
      <td className="num">{v ? fmt(v.kg) : ""}</td>
    </>
  );
}

export default KonteynerYuklemePage;
